import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@one-thd/sui-atomic-components';
import { SWEEPSTAKES_CONSTANTS } from '../../../core/Constants';
import '../styles/sweepstakes-fonts.scss';

const { TITLE: LINE1_STYLE, SUBTITLE: LINE2_STYLE } = SWEEPSTAKES_CONSTANTS.FONT_STYLES;

/**
 * Shared sweepstakes branded title rendered with custom FIFA /
 * Big Shoulders fonts.
 *
 * @param {string}  line1   - First header line (e.g. "FIFA WORLD CUP 2026™")
 * @param {string}  line2   - Second header line (e.g. "SWEEPSTAKES")
 * @param {boolean} inverse - Use inverse (white) text colour
 * @param {"card"|"drawer"} variant
 *   • "card"   – renders via <Typography> h1/h2 (inherits sizing from variant)
 *   • "drawer" – renders via <span> with explicit pixel sizing
 * @param {string}  testIdPrefix - prefix for data-testid attributes
 */
export const SweepstakesTitle = ({
  line1,
  line2,
  inverse,
  variant,
  testIdPrefix,
}) => {
  if (!line1 && !line2) return null;

  const upperLine1 = line1?.toUpperCase();
  const upperLine2 = line2?.toUpperCase();

  if (variant === 'card') {
    const color = inverse ? 'inverse' : undefined;
    return (
      <>
        {upperLine1 && upperLine2 && (
          <Typography
            style={{ fontFamily: '"THDFifaInline", sans-serif' }}
            color={color}
            variant="h1"
            data-testid={`${testIdPrefix}-title`}
          >
            {`${upperLine1} ${upperLine2}`}
          </Typography>
        )}
      </>
    );
  }

  // variant === 'drawer'
  // TODO this is dead code and never fires since the SweepstakesDrawer has it's own renderTitle implementation
  const textClass = inverse ? 'sui-block sui-text-inverse' : 'sui-block';
  return (
    <>
      {upperLine1 && (
        <span
          className={textClass}
          style={LINE1_STYLE}
          data-testid={`${testIdPrefix}-line1`}
        >
          {upperLine1}
        </span>
      )}
      {upperLine2 && (
        <span
          className={textClass}
          style={LINE2_STYLE}
          data-testid={`${testIdPrefix}-line2`}
        >
          {upperLine2}
        </span>
      )}
    </>
  );
};

SweepstakesTitle.propTypes = {
  line1: PropTypes.string,
  line2: PropTypes.string,
  inverse: PropTypes.bool,
  variant: PropTypes.oneOf(['card', 'drawer']),
  testIdPrefix: PropTypes.string,
};

SweepstakesTitle.defaultProps = {
  line1: null,
  line2: null,
  inverse: false,
  variant: 'drawer',
  testIdPrefix: 'sweepstakes',
};
